import { Button, FlexBox, Skeleton, Text } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { useCallback } from 'react';

import { ConversationAutomationAuditLogEntry } from 'apis/auditLogs';
import { flowsApi } from 'apis/flows';
import { useAppNavigate } from 'utils/url/useAppNavigate';

type RoutedThroughBusinessHoursProps = {
  node: ConversationAutomationAuditLogEntry<'AUTOMATION_BUSINESS_HOURS_NODE_VISITED'>;
};

export function RoutedThroughBusinessHours({
  node: { schedule_id: scheduleId, error },
}: RoutedThroughBusinessHoursProps): JSX.Element {
  const hasBusinessHoursAccess = useHasAccess('settings.business_hours');

  const navigate = useAppNavigate();

  const { isLoading, isFetching, isError, name } =
    flowsApi.useReadFlowScheduleQuery(
      {
        id: scheduleId,
      },
      {
        selectFromResult: ({ data, ...rest }) => {
          return {
            name: data?.name ?? '',
            ...rest,
          };
        },
        skip: !hasBusinessHoursAccess || !!error,
      },
    );

  const handleGoToBusinessHours = useCallback(() => {
    navigate({
      path: '/settings/businesshours/:id',
      params: {
        path: {
          id: scheduleId,
        },
      },
    });
  }, [scheduleId, navigate]);

  if (error) {
    return (
      <Text.Paragraph>
        Business hours · Failed with error: {error}
      </Text.Paragraph>
    );
  }

  if (isLoading || isFetching) {
    return <Skeleton height="18px" width="200px" />;
  }

  if (isError && hasBusinessHoursAccess) {
    return (
      <FlexBox margin="300 0">
        <Button variant="secondary" onClick={handleGoToBusinessHours}>
          Go to business hours
        </Button>
      </FlexBox>
    );
  }

  return (
    <Text.Paragraph>
      {`Business hours · Automation visited business hours schedule ${
        hasBusinessHoursAccess ? name : ''
      }`}
    </Text.Paragraph>
  );
}
